/* eslint-disable max-lines-per-function */
import stylesheet from './x-range-slider.component.scss?as=stylesheet';
import {html, LitElement, nothing} from 'lit';
import {createRef, ref} from 'lit/directives/ref.js';
import {classMap} from 'lit/directives/class-map.js';
import {defineCustomElement, formatNumber, formatPrice} from 'extensions/utils';
import noUiSlider from 'nouislider';
import uid from '@mvps-genesis/uid';

export class RangeSlider extends LitElement {
	static get styles () {
		return [stylesheet];
	}

	slider = createRef();

	formRef = createRef();

	minRef = createRef();

	maxRef = createRef();

	static get properties () {
		return {
			end: {
				type: Number
			},
			facet: {
				type: String
			},
			label: {
				type: String
			},
			max: {
				type: Number
			},
			maxFocused: {
				type: Boolean
			},
			min: {
				type: Number
			},
			minFocused: {
				type: Boolean
			},
			rangeType: {
				attribute: 'range-type',
				type: String
			},
			showInputs: {
				attribute: 'show-inputs',
				type: Boolean
			},
			start: {
				type: Number
			},
			value: {
				type: String
			}
		};
	}

	constructor () {
		super();

		this.end = 0;
		this.facet = '';
		this.label = '';
		this.max = 0;
		this.maxFocused = false;
		this.min = 0;
		this.minFocused = false;
		this.rangeType = '';
		this.showInputs = false;
		this.start = 0;
		this.uid = uid();
		this.value = '';
	}

	get sliderFormat () {
		return {
			from: value => {
				return value;
			},
			to: value => {
				return this.formatValue(value);
			}
		};
	}

	get sliderOptions () {
		return {
			ariaFormat: this.sliderFormat,
			connect: true,
			format: this.sliderFormat,
			handleAttributes: [
				{
					'aria-label': `Minimum ${this.label}`
				},
				{
					'aria-label': `Max ${this.label}`
				}
			],
			pips: {
				density: 99999,
				filter () {
					return 1;
				},
				format: this.sliderFormat,
				mode: 'positions',
				stepped: true,
				values: [
					0,
					100
				]
			},
			range: {
				max: this.max,
				min: this.min
			},
			start: [
				this.start,
				this.end
			]
		};
	}

	firstUpdated () {
		noUiSlider.create(
			this.slider.value,
			this.sliderOptions
		);

		// Bind change event listener
		this.slider.value.noUiSlider.on('change', this.handleSliderChange.bind(this));
		this.slider.value.noUiSlider.on('slide', this.handleSlide.bind(this));
	}

	updated () {
		this.slider.value.noUiSlider.updateOptions(this.sliderOptions);
	}

	handleSliderChange (_values, _handle, unencoded) {
		this.handleSlide(_values, _handle, unencoded);
		this.handleChange();
	}

	handleSlide (_values, _handle, unencoded) {
		this.start = unencoded[0];
		this.end = unencoded[1];
	}

	handleSubmit (e) {
		e.preventDefault();

		if (this.formRef.value.checkValidity() === false) {
			return false;
		}

		if (this.start === this.minRef?.value?.value && this.end === this.maxRef?.value?.value) {
			return false;
		}

		this.start = this.minRef?.value?.value;
		this.end = this.maxRef?.value?.value;
		this.handleChange();

		return false;
	}

	handleChange () {
		this.value = `${parseFloat(this.start).toFixed(2)},${parseFloat(this.end).toFixed(2)}`;

		this.dispatchEvent(new CustomEvent('change'));
	}

	handleInputChange () {
		this.formRef?.value?.dispatchEvent(new Event('submit', {
			bubbles: false,
			cancelable: true
		}));
	}

	handleMinFocus (focused) {
		this.minFocused = focused;
	}

	handleMaxFocus (focused) {
		this.maxFocused = focused;
	}

	handleKeyup (e) {
		switch (e.key) {
			case 'Enter':
				this.handleInputChange();
				break;
			default:
				break;
		}
	}

	formattedValue (value) {
		if (this.facet === 'jewelry_gem1_weight') {
			return parseFloat(value)?.toFixed(2);
		}

		return value;
	}

	renderInputs () {
		if (!this.showInputs) {
			return nothing;
		}

		const minClasses = {
			'input-container': true,
			'input-container-active': this.minFocused
		};

		const maxClasses = {
			'input-container': true,
			'input-container-active': this.maxFocused
		};

		return html`
			<form ${ref(this.formRef)} @submit="${this.handleSubmit}" class="inputs-form">
				<div class="${classMap(minClasses)}">
					<label for="${this.uid}-min" class="input-label">Min <span class="visually-hidden">${this.label}</span></label>
					<input
						${ref(this.minRef)}
						id="${this.uid}-min"
						class="input"
						type="number"
						min="${this.min}"
						max="${this.max}"
						step="any"
						required
						.value="${this.formattedValue(this.min > this.start ? this.min : this.start)}"
						@blur="${() => this.handleMinFocus(false)}"
						@focus="${() => this.handleMinFocus(true)}"
						@change="${this.handleInputChange}"
						@keyup="${this.handleKeyup}"
					/>
					<span tabindex="-1" aria-hidden="true" ?hidden="${this.minFocused}" class="input-overlay">${this.formatValue(this.min > this.start ? this.min : this.start)}</span>
				</div>
				<div class="${classMap(maxClasses)}">
					<label for="${this.uid}-max" class="input-label">Max <span class="visually-hidden">${this.label}</span></label>
					<input
						${ref(this.maxRef)}
						id="${this.uid}-max"
						class="input"
						type="number"
						min="${this.min}"
						max="${this.max}"
						step="any"
						required
						.value="${this.formattedValue(this.max < this.end ? this.max : this.end)}"
						@blur="${() => this.handleMaxFocus(false)}"
						@focus="${() => this.handleMaxFocus(true)}"
						@change="${this.handleInputChange}"
						@keyup="${this.handleKeyup}"
					/>
					<span tabindex="-1" aria-hidden="true" ?hidden="${this.maxFocused}" class="input-overlay">${this.formatValue(this.max < this.end ? this.max : this.end)}</span>
				</div>
				<button type="submit" aria-hidden="true" hidden>Submit</button>
			</form>
		`;
	}

	formatValue (value) {
		if (this.rangeType === 'currency') {
			return formatPrice(value);
		}

		return formatNumber(value);
	}

	render () {
		return html`
			${this.renderInputs()}
			<div ${ref(this.slider)}></div>
		`;
	}
};

defineCustomElement('x-range-slider', RangeSlider);
